import type { FC } from "react"
import { useMemo } from "react"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { Location, Order } from "@/graphql/codegen/graphql"
import { usePurchaseOrderSelectionTableColumns } from "@/screens/WarehouseReceiving/subfeatures/Incoming/hooks/usePurchaseOrderSelectionTableColumns"
import { isPurchaseOrderSelectable } from "@/screens/WarehouseReceiving/utils"

interface PurchaseOrderSelectionTableProps {
  purchaseOrders: Order[]
  onSelect: (purchaseOrders: Order[]) => void
  selectedPurchaseOrders: Order[]
  goodsReceiptDeliveryLocation?: Location | null
  isFetching: boolean
}

export const PurchaseOrderSelectionTable: FC<PurchaseOrderSelectionTableProps> = ({
  purchaseOrders,
  onSelect,
  selectedPurchaseOrders,
  goodsReceiptDeliveryLocation,
  isFetching,
}) => {
  const columns = usePurchaseOrderSelectionTableColumns()
  const rowSelection = useMemo(() => {
    return selectedPurchaseOrders.reduce(
      (acc, purchaseOrder) => {
        acc[purchaseOrder.orderId] = true
        return acc
      },
      {} as Record<string, boolean>
    )
  }, [selectedPurchaseOrders])

  const handleRowSelectionChange = (updater: any) => {
    if (typeof updater === "function") {
      const currentSelection = updater(rowSelection)
      const selectedKeys = Object.keys(currentSelection).filter((key) => currentSelection[key])

      const currentSelectedPurchaseOrders = purchaseOrders.filter((purchaseOrder) =>
        selectedKeys.includes(String(purchaseOrder.orderId))
      )

      const purchaseOrderIds = new Set(purchaseOrders.map((purchaseOrder) => String(purchaseOrder.orderId)))
      const previouslySelectedPurchaseOrders = selectedPurchaseOrders.filter(
        (purchaseOrder) => !purchaseOrderIds.has(String(purchaseOrder.orderId))
      )
      const allSelectedPurchaseOrders = [...previouslySelectedPurchaseOrders, ...currentSelectedPurchaseOrders]
      onSelect(allSelectedPurchaseOrders)
    }
  }

  return (
    <AerosBaseTable
      features={{
        tanstackOptions: {
          data: purchaseOrders,
          columns,
          onRowSelectionChange: handleRowSelectionChange,
          getRowId: (row) => String(row?.orderId),
          state: { rowSelection, isLoading: isFetching },
          enableRowSelection: (row) =>
            isPurchaseOrderSelectable(row.original, selectedPurchaseOrders, goodsReceiptDeliveryLocation),
          enableMultiRowSelection: true,
        },
      }}
      noDataMessage='No purchase orders found'
    />
  )
}
